import { useEffect, useState } from 'react';

import { normalizeBulletSpacing } from '@revelio/iso-utility';

import { formatDescriptionStreamRequest } from '../../../api/format-description-api';

type FormattedPostingDescription = {
  description: string;
  isFormatted: boolean;
  isFormatting: boolean;
};

const isAbortError = (error: unknown): boolean =>
  Boolean(
    error &&
      typeof error === 'object' &&
      'name' in error &&
      (error as { name: string }).name === 'AbortError'
  );

const HTML_ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&nbsp;': ' ',
  '&rsquo;': '’',
  '&lsquo;': '‘',
  '&rdquo;': '”',
  '&ldquo;': '“',
  '&ndash;': '–',
  '&mdash;': '—',
};

// Posting descriptions arrive as HTML. This is only the fallback for when the
// AI formatting endpoint is unavailable — it strips tags to readable text/markdown
// so we never show raw HTML. The happy path renders AI-formatted markdown.
const stripHtmlToText = (html: string): string => {
  const withBreaks = html
    .replace(/<\s*li[^>]*>/gi, '\n- ')
    .replace(/<\s*\/\s*(p|div|li|ul|ol|h[1-6]|tr)\s*>/gi, '\n')
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<[^>]+>/g, '');
  const decoded = withBreaks.replace(
    /&[a-z]+;|&#\d+;/gi,
    (entity) => HTML_ENTITIES[entity.toLowerCase()] ?? entity
  );
  return decoded
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+\n/g, '\n')
    .trim();
};

const toFallbackText = (raw: string): string =>
  normalizeBulletSpacing(stripHtmlToText(raw));

// Session-scoped cache of already-formatted descriptions, keyed by the trimmed
// raw description, so re-opening the same posting reveals it instantly instead
// of re-streaming. Bounded with FIFO eviction so browsing many postings can't
// grow it without limit.
const MAX_CACHED_DESCRIPTIONS = 100;
const formattedDescriptionCache = new Map<string, string>();

const readCachedDescription = (key: string): string | undefined =>
  formattedDescriptionCache.get(key);

const writeCachedDescription = (key: string, value: string): void => {
  // Re-insert so the most recently used key is the newest, keeping eviction FIFO
  // by recency of use.
  formattedDescriptionCache.delete(key);
  formattedDescriptionCache.set(key, value);
  if (formattedDescriptionCache.size > MAX_CACHED_DESCRIPTIONS) {
    const oldestKey = formattedDescriptionCache.keys().next().value;
    if (oldestKey !== undefined) {
      formattedDescriptionCache.delete(oldestKey);
    }
  }
};

/**
 * Formats a job-posting description for display by always sending it through the
 * AI formatting endpoint (descriptions are HTML, so deterministic formatting is
 * not reliable). Streams the result so markdown renders progressively — a
 * skeleton shows only until the first chunk arrives. If the endpoint fails, falls
 * back to HTML-stripped text so raw markup is never shown.
 */
export const useFormattedPostingDescription = (
  rawDescription: string
): FormattedPostingDescription => {
  const trimmed = rawDescription.trim();
  const shouldStream = trimmed.length > 0;

  const [streamedText, setStreamedText] = useState<string | null>(() =>
    shouldStream ? (readCachedDescription(trimmed) ?? null) : null
  );
  const [isStreaming, setIsStreaming] = useState<boolean>(
    () => shouldStream && readCachedDescription(trimmed) === undefined
  );

  useEffect(() => {
    if (!shouldStream) {
      setStreamedText(null);
      setIsStreaming(false);
      return;
    }

    // Already formatted this exact description earlier in the session — reveal it
    // immediately instead of re-streaming.
    const cached = readCachedDescription(trimmed);
    if (cached !== undefined) {
      setStreamedText(cached);
      setIsStreaming(false);
      return;
    }

    let isCancelled = false;
    const controller = new AbortController();
    setStreamedText('');
    setIsStreaming(true);

    formatDescriptionStreamRequest({
      text: trimmed,
      descriptionType: 'job_posting',
      signal: controller.signal,
      onChunk: (accumulated) => {
        if (!isCancelled) {
          setStreamedText(accumulated);
        }
      },
    })
      .then((full) => {
        const normalized = normalizeBulletSpacing(full);
        writeCachedDescription(trimmed, normalized);
        if (!isCancelled) {
          setStreamedText(normalized);
        }
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }
        if (!isAbortError(error)) {
          console.error('Failed to format posting description:', error);
        }
        setStreamedText((current) =>
          current && current.length > 0 ? current : null
        );
      })
      .finally(() => {
        if (!isCancelled) {
          setIsStreaming(false);
        }
      });

    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [trimmed, shouldStream]);

  if (shouldStream && streamedText && streamedText.length > 0) {
    // Render partial markdown progressively while the rest streams in.
    return {
      description: streamedText,
      isFormatted: true,
      isFormatting: false,
    };
  }

  return {
    description: toFallbackText(trimmed),
    isFormatted: false,
    isFormatting: shouldStream && isStreaming,
  };
};
